import { useEffect, useState, useMemo } from "react";
import { Table, Badge, Spinner, Button } from "reactstrap";
import { saveAs } from "file-saver";
import { getDepositVsRentReport, exportDepositVsRentExcel } from "../TenantForm/Helpers/api";

const fmt = (v) =>
  v === null || v === undefined || isNaN(Number(v))
    ? "—"
    : Number(v).toLocaleString("en-US", { style: "currency", currency: "USD" });

const STATUS = {
  OVER: { color: "danger", label: "Over-collected" },
  COMPLIANT: { color: "success", label: "Compliant" },
  MISSING: { color: "warning", label: "No data" },
};

const DepositVsRentTab = ({ masterSheetData }) => {
  const mastersheetId = masterSheetData?._id;
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [onlyOver, setOnlyOver] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let active = true;
    if (!mastersheetId) {
      setReport(null);
      return;
    }
    setLoading(true);
    setError(null);
    getDepositVsRentReport(mastersheetId)
      .then((resp) => {
        if (!active) return;
        if (resp?.success) setReport(resp.data);
        else setError(resp?.message || "Failed to load report");
      })
      .catch((e) => {
        if (active) setError(e?.response?.data?.message || "Failed to load report");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [mastersheetId]);

  const visibleRows = useMemo(() => {
    if (!report?.rows) return [];
    return onlyOver ? report.rows.filter((r) => r.status === "OVER") : report.rows;
  }, [report, onlyOver]);

  const handleDownload = async () => {
    if (!mastersheetId) return;
    setDownloading(true);
    try {
      const blob = await exportDepositVsRentExcel(mastersheetId);
      const safe = (report?.building_name || "report").replace(/[^a-z0-9_-]+/gi, "_");
      saveAs(blob, `SecurityDeposit-vs-Rent_${safe}.xlsx`);
    } catch (e) {
      setError("Failed to download Excel.");
    } finally {
      setDownloading(false);
    }
  };

  if (loading)
    return (
      <div className="text-center p-4">
        <Spinner color="primary" /> Building report…
      </div>
    );
  if (error) return <p className="text-danger text-center">{error}</p>;
  if (!report) return <p className="text-muted text-center">No data available.</p>;

  const t = report.totals;

  return (
    <div>
      <div className="d-flex flex-wrap gap-3 mb-3">
        <div className="border rounded p-2 px-3">
          <div className="small text-muted">Units assessed</div>
          <strong>{t.unitsTotal}</strong>
        </div>
        <div className="border rounded p-2 px-3" style={{ background: "#fdeaea" }}>
          <div className="small text-muted">Over-collected</div>
          <strong>{t.over}</strong> · <span>{fmt(t.reimbursementOwed)} owed</span>
        </div>
        <div className="border rounded p-2 px-3">
          <div className="small text-muted">Compliant</div>
          <strong>{t.compliant}</strong>
        </div>
        <div className="border rounded p-2 px-3">
          <div className="small text-muted">No data</div>
          <strong>{t.missing}</strong>
        </div>
      </div>

      <div className="d-flex align-items-center mb-2">
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="sdRentOnlyOver"
            checked={onlyOver}
            onChange={(e) => setOnlyOver(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="sdRentOnlyOver">
            Show only over-collected
          </label>
        </div>
        <Button color="success" className="ms-auto" onClick={handleDownload} disabled={downloading || !mastersheetId}>
          {downloading ? <Spinner size="sm" /> : "Download Excel"}
        </Button>
      </div>

      <Table hover responsive className="table-sm">
        <thead className="table-light">
          <tr>
            <th>Unit #</th>
            <th>Tenant</th>
            <th className="text-end">Basic Rent</th>
            <th className="text-end">Security Deposit</th>
            <th className="text-end">Difference</th>
            <th className="text-end">Reimburse</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.length ? (
            visibleRows.map((r, i) => (
              <tr key={`${r.unitNo}-${i}`} className={r.status === "OVER" ? "table-danger" : ""}>
                <td>
                  <strong>{r.unitNo}</strong>
                </td>
                <td>{r.tenant}</td>
                <td className="text-end">{fmt(r.rent)}</td>
                <td className="text-end">{fmt(r.deposit)}</td>
                <td className="text-end">{r.difference === null ? "—" : fmt(r.difference)}</td>
                <td className="text-end">{r.reimbursement ? fmt(r.reimbursement) : "—"}</td>
                <td>
                  <Badge color={STATUS[r.status]?.color ?? "secondary"}>{STATUS[r.status]?.label ?? r.status}</Badge>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={7} className="text-center text-muted">
                No units to display.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};

export { DepositVsRentTab };
